import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";

interface AuthGateProps {
  children: (session: Session) => ReactNode;
}

type Mode = "signin" | "signup" | "forgot" | "recovery";

export function AuthGate({ children }: AuthGateProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supabase) return;
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (active) {
        setSession(data.session);
        setReady(true);
      }
    });
    const { data } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      setReady(true);
      if (event === "PASSWORD_RECOVERY") setMode("recovery");
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!supabase) return;
    setBusy(true);
    setMessage("");
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      } else if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin,
          },
        });
        if (error) throw error;
        if (!data.session) {
          setMessage(
            "驗證電郵已寄出。請完成驗證後返回登入。 / Check your email to verify the account.",
          );
        }
      } else if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/?recovery=1`,
        });
        if (error) throw error;
        setMessage("密碼重設連結已寄出。 / Password reset link sent.");
      } else {
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
        setMessage("密碼已更新。 / Password updated.");
        setMode("signin");
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Authentication failed");
    } finally {
      setBusy(false);
    }
  }

  if (!ready) {
    return (
      <main className="auth-screen" aria-busy="true">
        <p className="eyebrow">INVOICE INDEX / AUTH</p>
        <h1>RESTORING SESSION…</h1>
      </main>
    );
  }

  if (session) return children(session);

  return (
    <main className="auth-screen">
      <section className="auth-panel" aria-labelledby="auth-title">
        <div className="brand-mark auth-mark" aria-hidden="true">
          <i />
          <i />
        </div>
        <p className="eyebrow">INVOICE INDEX · BILLING SYSTEM</p>
        <h1 id="auth-title">
          {mode === "signin" && "WELCOME BACK"}
          {mode === "signup" && "CREATE ACCOUNT"}
          {mode === "forgot" && "RESET PASSWORD"}
          {mode === "recovery" && "SET NEW PASSWORD"}
        </h1>
        <p className="auth-intro">
          精準、可靠的發票工作台。
          <br />
          A focused invoice workspace for independent professionals.
        </p>

        <form onSubmit={(event) => void submit(event)} className="auth-form">
          {mode !== "recovery" && (
            <label>
              <span>EMAIL / 電郵</span>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </label>
          )}
          {mode !== "forgot" && (
            <label>
              <span>
                {mode === "recovery"
                  ? "NEW PASSWORD / 新密碼"
                  : "PASSWORD / 密碼"}
              </span>
              <input
                type="password"
                autoComplete={
                  mode === "signin" ? "current-password" : "new-password"
                }
                minLength={8}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>
          )}
          {message && (
            <p className="form-message" role="status">
              {message}
            </p>
          )}
          <button className="button primary" disabled={busy} type="submit">
            {busy
              ? "PLEASE WAIT…"
              : mode === "signin"
                ? "SIGN IN / 登入"
                : mode === "signup"
                  ? "CREATE ACCOUNT / 註冊"
                  : mode === "forgot"
                    ? "SEND RESET LINK"
                    : "UPDATE PASSWORD"}
          </button>
        </form>

        <div className="auth-links">
          {mode !== "signin" && (
            <button type="button" onClick={() => setMode("signin")}>
              BACK TO SIGN IN
            </button>
          )}
          {mode === "signin" && (
            <>
              <button type="button" onClick={() => setMode("signup")}>
                CREATE ACCOUNT
              </button>
              <button type="button" onClick={() => setMode("forgot")}>
                FORGOT PASSWORD
              </button>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
